import { useState, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { useFetchCreditUsage, PeriodType } from "hooks";

type Period = "Monthly" | "Weekly" | "Daily";

const periodMap: Record<Period, PeriodType> = {
  Monthly: "30_days",
  Weekly: "7_days",
  Daily: "24_hours",
};

const CreditUsageChart = () => {
  const [period, setPeriod] = useState<Period>("Weekly");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const { data: creditUsageData, isLoading } = useFetchCreditUsage(periodMap[period]);

  const periods: Period[] = ["Daily", "Weekly", "Monthly"];

  // Transform API data to chart format
  const chartData = useMemo(() => {
    if (!creditUsageData?.data?.graph_plots) return [];

    const maxValue = Math.max(
      ...creditUsageData.data.graph_plots.map(
        (plot) => plot.plot_values.credits_consumed
      ),
      100
    );

    return creditUsageData.data.graph_plots.map((plot) => ({
      month: plot.plot_name,
      value: plot.plot_values.credits_consumed,
      remaining: maxValue - plot.plot_values.credits_consumed,
      maxValue,
    }));
  }, [creditUsageData]);

  const maxValue = chartData[0]?.maxValue || 400;

  return (
    <div className="bg-white rounded-2xl p-6 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="flex justify-between items-center mb-6 border-b border-[#F2F4F7] pb-4">
        <h3 className="text-lg font-semibold text-gray-900">Credit Usage</h3>
        <div className="relative">
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 hover:text-gray-900"
          >
            {period}
            <svg
              className={`w-4 h-4 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {isDropdownOpen && (
            <div className="absolute right-0 mt-1 w-32 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
              {periods.map((p) => (
                <button
                  key={p}
                  onClick={() => {
                    setPeriod(p);
                    setIsDropdownOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                    period === p ? "text-[#012169] font-medium" : "text-gray-600"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Chart */}
      {isLoading ? (
        <div className="h-[280px] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#012169] mx-auto mb-4"></div>
            <p className="text-gray-600">Loading usage data...</p>
          </div>
        </div>
      ) : chartData.length === 0 ? (
        <div className="h-[280px] flex items-center justify-center">
          <p className="text-gray-500">No usage data available</p>
        </div>
      ) : (
        <div className="sm:h-[280px] h-[300px] lg:w-[105%] w-[110%] lg:-ml-[5%] -ml-[4%] overflow-hidden">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
              <XAxis
                dataKey="month"
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#9ca3af", fontSize: 10 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: "#9ca3af", fontSize: 10 }}
                domain={[0, maxValue]}
              />
              <Bar
                dataKey="value"
                stackId="stack"
                fill="#1570EF"
                barSize={12}
                radius={[10, 10, 10, 10]}
              />
              <Bar
                dataKey="remaining"
                stackId="stack"
                fill="#F2F7FF"
                barSize={12}
                radius={[10, 10, 10, 10]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}
    </div>
  );
};

export { CreditUsageChart };
